import React, { useEffect, useState } from 'react';
import { Star, Loader2, ShieldCheck, MessageSquare, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ value, size = 'w-4 h-4' }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`${size} ${n <= Math.round(value) ? 'fill-[#D47A4A] text-[#D47A4A]' : 'text-[#E7E5E4]'}`}
      />
    ))}
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
};

const ProductReviews = ({ productId, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!productId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/tracking/reviews/product/${productId}`);
        setData(res.data);
      } catch (e) {
        console.warn('Failed to load product reviews:', e.message);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8BA58F]" data-testid="reviews-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando opiniones…
      </div>
    );
  }

  if (loadError && !compact) {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 text-sm text-orange-800" data-testid="reviews-error">
        No pudimos cargar las opiniones en este momento. Intenta recargar la página.
      </div>
    );
  }
  if (loadError && compact) return null;

  if (!data || data.stats.count === 0) {
    if (compact) return null;
    return (
      <div className="bg-[#F7F2E8]/50 rounded-2xl p-6 text-center" data-testid="reviews-empty">
        <MessageSquare className="w-8 h-8 text-[#8BA58F] mx-auto mb-2" />
        <p className="text-sm text-[#57534E]">Aún no hay reseñas verificadas para este producto.</p>
        <p className="text-xs text-[#8BA58F] mt-1">¡Sé el primero en compartir tu experiencia!</p>
      </div>
    );
  }

  const { stats, reviews } = data;
  const shown = expanded ? reviews : reviews.slice(0, 3);

  // Compact = show only the average + count inline (used near price)
  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="reviews-compact">
        <Stars value={stats.average} />
        <span className="font-semibold text-[#1C1917]">{stats.average.toFixed(1)}</span>
        <span className="text-[#8BA58F]">
          ({stats.count} {stats.count === 1 ? 'opinión verificada' : 'opiniones verificadas'})
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="product-reviews">
      {/* Summary card */}
      <div className="bg-white border border-[#E7E5E4] rounded-2xl p-5 flex flex-wrap items-center gap-4">
        <div>
          <p className="font-display font-bold text-4xl text-[#1C1917]">{stats.average.toFixed(1)}</p>
          <Stars value={stats.average} size="w-5 h-5" />
        </div>
        <div className="text-sm text-[#57534E] border-l border-[#F5F5F4] pl-4">
          <p className="font-medium text-[#1C1917]">
            {stats.count} {stats.count === 1 ? 'reseña verificada' : 'reseñas verificadas'}
          </p>
          <p className="text-xs text-[#8BA58F] flex items-center gap-1 mt-0.5">
            <ShieldCheck className="w-3 h-3" /> De clientes que compraron el producto
          </p>
        </div>
      </div>

      {/* Individual reviews */}
      <div className="space-y-3">
        {shown.map((r) => (
          <div key={r.id} className="bg-white border border-[#E7E5E4] rounded-2xl p-5" data-testid={`review-${r.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <Stars value={r.rating} />
                {r.title && <p className="font-semibold text-[#1C1917] mt-1">{r.title}</p>}
              </div>
              <div className="text-right">
                <p className="text-xs text-[#57534E]">{r.customer_name || 'Cliente'}</p>
                <p className="text-[10px] text-[#8BA58F]">{fmtDate(r.created_at)}</p>
              </div>
            </div>
            {r.comment && <p className="text-sm text-[#57534E] leading-relaxed mt-2">{r.comment}</p>}
            {r.images && r.images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid={`review-photos-${r.id}`}>
                {r.images.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightbox({ url: img })}
                    className="w-20 h-20 rounded-xl overflow-hidden border border-[#E7E5E4] hover:border-[#2A5C43] transition-colors"
                  >
                    <img
                      src={`${BACKEND}${img}?size=400`}
                      alt={`Foto de reseña ${idx + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
            {r.verified_purchase && (
              <p className="text-[10px] text-[#2A5C43] font-semibold uppercase tracking-widest mt-2 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Compra verificada
              </p>
            )}
          </div>
        ))}
      </div>

      {reviews.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-[#2A5C43] hover:underline font-medium"
          data-testid="reviews-toggle"
        >
          {expanded ? 'Ver menos' : `Ver todas las ${reviews.length} reseñas`}
        </button>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="product-review-lightbox"
        >
          <button
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
            onClick={() => setLightbox(null)}
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={`${BACKEND}${lightbox.url}?size=1200`}
            alt="Reseña"
            className="max-h-[90vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
export { Stars };
